import React, { useState, useEffect } from "react";
import { Pie, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { getInterestedDomainPieChart } from "../../../services/domain.services";

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const Statistics = () => {
  const [activeTab, setActiveTab] = useState("pie");
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [
          "#4c9aff", // Blue
          "#ff00a0", // Pink
          "#a3e635", // Green
          "#ff5722", // Orange
          "#ffeb3b", // Yellow
          "#8b5cf6", // Purple
          "#ec4899", // Pink
          "#10b981", // Emerald
          "#f59e0b", // Amber
          "#ef4444", // Red
        ],
        borderColor: "rgba(255, 255, 255, 0.6)", // White borders for better contrast
        borderWidth: 2,
        hoverOffset: 15,
      },
    ],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalStudents, setTotalStudents] = useState(0);
  const [topDomain, setTopDomain] = useState({ name: "", percentage: 0 });

  // Options for the Pie Chart
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          color: "#374151", // Dark gray legend text
          font: {
            size: 14,
            weight: "bold",
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
        },
        title: {
          display: true,
          text: "Career Domains",
          color: "#111827",
          font: {
            weight: "bold",
            size: 16,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(17, 24, 39, 0.8)",
        padding: 12,
        bodyFont: {
          size: 14,
        },
        bodyColor: "#ffffff",
        titleFont: {
          size: 16,
          weight: "bold",
        },
        titleColor: "#ffffff",
        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.label}: ${tooltipItem.formattedValue}%`;
          },
        },
      },
    },
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: 10,
        right: 120, // Extra padding for legend
      },
    },
    elements: {
      arc: {
        borderWidth: 2,
      },
    },
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 2000,
    },
  };

  // Options for the Doughnut Chart
  const doughnutOptions = {
    ...pieOptions,
    cutout: "60%", // Creates the doughnut hole
    plugins: {
      ...pieOptions.plugins,
      tooltip: {
        ...pieOptions.plugins.tooltip,
      },
    },
  };

  // Options for the Bar Chart
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y", // Horizontal bar chart
    plugins: {
      legend: {
        display: false, // Hide legend for bar chart
      },
      tooltip: {
        backgroundColor: "rgba(17, 24, 39, 0.8)",
        padding: 12,
        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.formattedValue}%`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        grid: {
          display: true,
          color: "rgba(107, 114, 128, 0.1)",
        },
        ticks: {
          callback: function (value) {
            return value + "%";
          },
          color: "#6B7280",
        },
        title: {
          display: true,
          text: "Percentage of Students",
          color: "#374151",
          font: {
            weight: "bold",
            size: 14,
          },
          padding: { top: 10, bottom: 10 },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#374151",
          font: {
            weight: "bold",
          },
        },
      },
    },
    animation: {
      duration: 2000,
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getInterestedDomainPieChart();

        // Process the data for the chart
        if (response && response.data) {
          const filteredData = response.data.filter((item) => item.count > 0);
          const sortedData = [...filteredData].sort(
            (a, b) => b.count - a.count
          );

          const total = sortedData.reduce((sum, item) => sum + item.count, 0);
          setTotalStudents(response.totalStudents || total);

          const recalculatedData = sortedData.map((item) => ({
            ...item,
            percentage: total ? Math.round((item.count / total) * 100) : 0,
          }));

          if (recalculatedData.length > 0) {
            setTopDomain({
              name: recalculatedData[0].domain_name,
              percentage: recalculatedData[0].percentage,
            });
          }

          setChartData({
            labels: recalculatedData.map((item) => item.domain_name),
            datasets: [
              {
                ...chartData.datasets[0],
                data: recalculatedData.map((item) => item.percentage),
              },
            ],
          });
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching domain data:", err);
        setError("Failed to load domain statistics");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get data formatted for the bar chart
  const getBarChartData = () => {
    return {
      labels: chartData.labels,
      datasets: [
        {
          label: "Student Interest (%)",
          data: chartData.datasets[0].data,
          backgroundColor: chartData.datasets[0].backgroundColor.slice(
            0,
            chartData.labels.length
          ),
          borderColor: "rgba(255, 255, 255, 0.6)",
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  };

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 max-w-6xl mx-auto">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          Career Domain Interest Statistics
        </h1>
        <p className="text-gray-600">
          Visual breakdown of student interest across different career domains
        </p>
      </header>

      {/* Stats Cards */}
      {!loading && !error && chartData.labels.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-100">
            <div className="text-sm font-medium text-purple-500 mb-1">
              Total Students
            </div>
            <div className="text-2xl font-bold text-purple-800">
              {formatNumber(totalStudents)}
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-100">
            <div className="text-sm font-medium text-blue-500 mb-1">
              Different Career Domains
            </div>
            <div className="text-2xl font-bold text-blue-800">
              {chartData.labels.length}
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-100">
            <div className="text-sm font-medium text-green-500 mb-1">
              Most Popular Domain
            </div>
            <div
              className="text-2xl font-bold text-green-800 truncate"
              title={topDomain.name}
            >
              {topDomain.name}{" "}
              <span className="text-lg text-green-600">
                ({topDomain.percentage}%)
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Chart Type Tabs */}
      {!loading && !error && chartData.labels.length > 0 && (
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab("pie")}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === "pie"
                ? "border-b-2 border-purple-500 text-purple-700"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Pie Chart
          </button>
          <button
            onClick={() => setActiveTab("doughnut")}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === "doughnut"
                ? "border-b-2 border-purple-500 text-purple-700"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Doughnut Chart
          </button>
          <button
            onClick={() => setActiveTab("bar")}
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === "bar"
                ? "border-b-2 border-purple-500 text-purple-700"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Bar Chart
          </button>
        </div>
      )}

      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
        {/* Conditional rendering based on data loading state */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mb-4"></div>
            <p className="text-gray-600 font-medium">
              Loading domain statistics...
            </p>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {error}
          </div>
        ) : chartData.labels.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mb-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className="text-center font-medium">No domain data available</p>
            <p className="text-center text-sm mt-2">
              There are currently no statistics to display.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-full md:w-3/4 lg:w-2/3 h-80">
              {activeTab === "pie" && (
                <Pie data={chartData} options={pieOptions} />
              )}
              {activeTab === "doughnut" && (
                <Doughnut data={chartData} options={doughnutOptions} />
              )}
              {activeTab === "bar" && (
                <Bar data={getBarChartData()} options={barOptions} />
              )}
            </div>

            {/* Data Table (optional - shows data in tabular format) */}
            <div className="mt-8 w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Domain
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chartData.labels.map((label, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="h-3 w-3 rounded-full mr-2"
                            style={{
                              backgroundColor:
                                chartData.datasets[0].backgroundColor[index],
                            }}
                          ></div>
                          <div className="text-sm font-medium text-gray-900">
                            {label}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {chartData.datasets[0].data[index]}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Statistics;
